import { HoverEffect } from '@/components/ui/card-hover-effect';
import { BackgroundBeams } from '@/components/ui/background-beams';
import { 
  FaGraduationCap, 
  FaHardHat, 
  FaFutbol, 
  FaHeartbeat, 
  FaHotel, 
  FaStore 
} from 'react-icons/fa';
import { resolveIcon } from '@/sanity/iconMap';
import type { SectorsSectionData } from '@/sanity/types';

// R2 - Icon style for consistency across all sectors
const iconClass = "w-8 h-8";

// R2 - Sectors data with premium icons
const SECTORS = [
  {
    title: 'Educación',
    description: 'Mobiliario escolar y universitario que inspira aprendizaje, colaboración y creatividad en cada aula.',
    icon: <FaGraduationCap className={iconClass} />,
  },
  {
    title: 'Construcción',
    description: 'Soluciones robustas y versátiles para proyectos constructivos de cualquier envergadura.',
    icon: <FaHardHat className={iconClass} />,
  },
  {
    title: 'Ocio & Deporte',
    description: 'Espacios dinámicos y funcionales que potencian experiencias de entretenimiento y bienestar.',
    icon: <FaFutbol className={iconClass} />,
  },
  {
    title: 'Salud',
    description: 'Mobiliario higiénico, ergonómico y confortable diseñado para centros sanitarios exigentes.',
    icon: <FaHeartbeat className={iconClass} />,
  },
  {
    title: 'Hotelería',
    description: 'Diseño premium que eleva experiencias de hospitalidad con lujo y confort excepcional.',
    icon: <FaHotel className={iconClass} />,
  },
  {
    title: 'Retail',
    description: 'Espacios comerciales optimizados que atraen clientes y generan experiencias memorables.',
    icon: <FaStore className={iconClass} />,
  },
];

interface SectorsSectionProps {
  data?: SectorsSectionData | undefined;
}

/**
 * R2 - Sectors section with interactive HoverEffect cards
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function SectorsSection({ data }: SectorsSectionProps) {
  const sectionTitle = data?.title ?? 'Sectores que Servimos';
  const sectionDesc = data?.description ?? 'Soluciones especializadas para cada industria, adaptadas a sus necesidades únicas';

  const resolvedSectors = data?.items?.length
    ? data.items.map(item => ({
        title: item.title,
        description: item.description,
        icon: resolveIcon(item.iconName),
      }))
    : SECTORS;
  return (
    <section className="py-20 sm:py-32 bg-gray-50 relative overflow-hidden">
      {/* Animated Background */}
      <BackgroundBeams />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            {sectionTitle}
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {sectionDesc}
          </p>
        </div>

        {/* Interactive Sectors Grid with HoverEffect */}
        <HoverEffect items={resolvedSectors} className="gap-6" />
      </div>
    </section>
  );
}
